import { CompetencyMapQuery, CompetencyMapQueryResult, useCompetencyMapQuestionsQuery } from "@generated/graphql";
import { Box } from "@mui/material";
import CircularLoading from "components/CircularLoading";
import withHeightFitContent from "hocs/withHeightFitContent";
import { useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import QuestionsList from "./QuestionList";
import QuestionSelectionActions from "./QuestionSelectionActions";
import { extractQuestions } from "./utils";

const QuestionsListWithHeightFitContent = withHeightFitContent(QuestionsList);
const selectionModeMaxHeightOffset = { xs: 128, lg: 96 };

interface QuestionsListTabProps {
  heightFitContentSignal?: unknown;
  competencyMap: CompetencyMapQuery["competencyMap"];
  selectionMode?: boolean;
  refetchCompetencyMap: CompetencyMapQueryResult["refetch"];
}

const QuestionsListTab = ({
  heightFitContentSignal,
  competencyMap: competencyMapView,
  selectionMode,
  refetchCompetencyMap,
}: QuestionsListTabProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { mapId } = useParams();
  const { data, loading, refetch } = useCompetencyMapQuestionsQuery({
    variables: { competencyMapId: mapId! },
    skip: !mapId,
    fetchPolicy: "no-cache",
  });

  const competencyMapQuestions = data?.competencyMap;
  const questions = useMemo(() => extractQuestions(competencyMapQuestions), [competencyMapQuestions]);

  if (loading) return <CircularLoading />;

  return (
    <Box>
      <QuestionsListWithHeightFitContent
        competencyMap={competencyMapQuestions}
        refetchCompetencyMapQuestions={refetch}
        refetchCompetencyMap={refetchCompetencyMap}
        refProp={ref}
        additionalMaxHeightOffset={selectionMode ? selectionModeMaxHeightOffset : undefined}
        recalculateSignal={[heightFitContentSignal, selectionMode]}
      />
      <QuestionSelectionActions
        competencyMap={competencyMapView}
        questions={questions}
        refetchCompetencyMapQuestions={refetch}
      />
    </Box>
  );
};

export default QuestionsListTab;
